import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api } from "../../lib/api";
import { useClientContext } from "./ClientWorkspace";
import { InstallLinkButton } from "./InstallLinkButton";
import { useConfirm } from "../../components/primitives/Confirm";
import { Button } from "../../components/primitives/Button";
import { CopyButton } from "../../components/primitives/CopyButton";
import { FormField, TextInput } from "../../components/primitives/FormField";
import { SLUG_RE } from "../client-rows";

/**
 * Settings tab (spec §3.3): the rare and the dangerous, kept off the tabs CS
 * uses daily.
 *
 * Rename is reversible, so it is Tier 0 behind an explicit Save. Deleting a
 * client cannot be undone, which is why it is Tier 2 and asks for the client's
 * name typed back.
 */
export function ClientSettings() {
  const { org, env, environments, allConnections, allPipelines, refetchOrganizations } = useClientContext();
  const nav = useNavigate();
  const confirm = useConfirm();

  const [name, setName] = useState(org.name);
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Follow the server name only while the field still shows it; an edit in
  // progress is never clobbered by a poll landing mid-typing.
  const lastServerName = useRef(org.name);
  useEffect(() => {
    const previous = lastServerName.current;
    lastServerName.current = org.name;
    setName((current) => (current === previous ? org.name : current));
  }, [org.name]);

  const dirty = name.trim() !== org.name;

  /** Rename the client, refreshing the workspace so the header agrees at once. */
  async function save() {
    const next = name.trim();
    if (!SLUG_RE.test(next)) {
      setSaveError("Name must be lowercase letters, digits and hyphens, 3 to 63 characters.");
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await api.updateOrganization(org.id, { name: next });
      await refetchOrganizations();
      setSaveNote("Saved.");
      setTimeout(() => setSaveNote(null), 2000);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  /** Delete the client and everything under it. Tier 2 — the body counts exactly
   *  what goes, and what stays behind in the destination. */
  async function remove() {
    const c = allConnections.length;
    const p = allPipelines.length;
    const ok = await confirm({
      tier: 2,
      title: `Delete ${org.name}?`,
      body: `This removes the client, its ${c} ${c === 1 ? "connection" : "connections"}, ${p} ${p === 1 ? "pipeline" : "pipelines"} and all run history. Records already written to HubSpot stay where they are. This cannot be undone.`,
      confirmLabel: `Delete ${org.name}`,
      typedPhrase: org.name,
      onConfirm: async () => { await api.deleteOrganization(org.id); },
    });
    if (ok) nav("/clients");
  }

  return (
    <div className="settings">
      <section className="settings__section">
        <div className="uppercase-label settings__title">NAME</div>
        <form onSubmit={(e) => { e.preventDefault(); void save(); }} noValidate className="settings__row">
          <FormField label="Client name" hint="Lowercase, hyphenated. Shown everywhere in Anansi.">
            <TextInput aria-label="Client name" value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <Button type="button" variant="primary" onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
          {saveNote ? <span className="settings__note">{saveNote}</span> : null}
        </form>
        {saveError ? <div className="field__error">{saveError}</div> : null}
      </section>

      <section className="settings__section">
        <div className="uppercase-label settings__title">JUMP ORGANIZATION ID</div>
        <div className="settings__row">
          <TextInput aria-label="Jump organization id" value={org.id} readOnly className="field__input mono settings__id" />
          <CopyButton value={org.id} label="Copy id" />
        </div>
        <p className="page-help">
          Events from Jump carry this id and Anansi uses it to route them to this client. It cannot be changed here.
        </p>
      </section>

      <section className="settings__section">
        <div className="uppercase-label settings__title">ENVIRONMENTS</div>
        {environments.length === 0 ? (
          <p className="page-help">No environments registered for this client.</p>
        ) : (
          <ul className="settings__list mono">
            {environments.map((e) => (
              <li key={e.name}>{e.name}{e.disabled ? " (paused)" : ""}</li>
            ))}
          </ul>
        )}
        <p className="page-help">
          Manage environments on the <Link to="/environments">Environments page</Link>.
        </p>
      </section>

      <section className="settings__section">
        <div className="uppercase-label settings__title">HUBSPOT INSTALL LINK</div>
        <InstallLinkButton organizationId={org.id} environments={environments} defaultEnvironment={env} />
        <p className="page-help">Send this link to the client. Installing the Anansi app in their HubSpot creates the connection.</p>
      </section>

      <section className="settings__section">
        <div className="uppercase-label settings__title">PIPELINES</div>
        <Link to={`/pipelines/new?organizationId=${encodeURIComponent(org.id)}`}>
          <Button variant="secondary">New pipeline</Button>
        </Link>
      </section>

      <section className="settings__section danger-zone">
        <div className="uppercase-label settings__title">DANGER ZONE</div>
        <p className="page-help">Deleting a client removes its connections, pipelines and run history.</p>
        <Button variant="danger" onClick={() => void remove()}>Delete client</Button>
      </section>
    </div>
  );
}
